
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CartItem from "./CartItem";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import { applyCouponInCart, getCart } from "../../State/Cart/Action";
import RecentReviewProductsCarousel from "../RecentReviewProductsCarousel/RecentReviewProductsCarousel";
import HomeSectionGrid from './../Home/HomeSectionGrid';
import { getAllProducts } from "../../State/Product/Action";
import EmptyState from "../AccountComponents/EmptyState";
import Loader from "../Loader/Loader";

// NOTE: Replace these paths with your own static assets
const PAYMENT_ICONS = [
  { src: "https://www.citypng.com/public/uploads/preview/download-visa-card-logo-icon-png-735811696866915avdywnhoab.png", alt: "Visa" },
  { src: "https://www.citypng.com/public/uploads/preview/png-mastercard-payment-logo-icon-701751695036654sl5blladjo.png", alt: "Mastercard" },
  { src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcm2oPQT2XfjwWV3MQUjsJViP1sMcEUxBK6Q&s", alt: "American Express" },
  { src: "https://www.citypng.com/public/uploads/preview/hd-paypal-logo-transparent-background-701751694777793hs5bxpddux.png", alt: "PayPal" },
];

export default function Cart() {
  const [couponCode, setCouponCode] = useState("");
  const [isGiftItem, setIsGiftItem] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { cart } = useSelector((store) => store);
  const [isLoading, setIsLoading] = useState(false);
  const [isCouponApply, setIsCouponApply] = useState(false);
  const { convert, selectedCurrency } = useConvertPrice();
  const products = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(getAllProducts());
  }, [dispatch]);

  const handleCheckout = () => {
    setIsLoading(true);
    setTimeout(() => {
      navigate(`/cart/checkout`, { state: { id: cart?.id } });
    }, 200);
  };

  useEffect(() => {
    if (jwt) {
      dispatch(getCart());
    }
  }, [dispatch, cart?.addCartItem, cart?.deleteCartItem, cart?.updateCartItem, jwt]);

  const handleApplyCoupon = async () => {
    if (couponCode.trim()) {
      setIsLoading(true);
      try {
        await dispatch(applyCouponInCart(couponCode, setIsCouponApply));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const summaryCard = "rounded-2xl border border-[#ececec] bg-white";
  const summaryLabel = "font-sans text-[14px] font-light text-[#333]";
  const summaryValue = "font-sans text-[14px] font-normal text-[#333]";

  return (
    <Box className="min-h-screen py-8 font-sans text-[#333]">
      {(cart?.addLoading || cart?.loading) ? <Loader /> : (
        cart?.cartItems?.length > 0 ? (
          <Box className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: { xs: 22, md: 24 },
                    fontWeight: 500,
                    color: "#222",
                    mb: 2,
                  }}
                >
                  Your Shopping Cart
                </Typography>
                <Box className={`${summaryCard} overflow-hidden divide-y divide-[#ececec]`}>
                  {cart?.cartItems?.map((item, index) => (
                    <CartItem key={item?.id || index} item={item} />
                  ))}
                </Box>

                <Box className="mt-4 flex items-center justify-end gap-2">
                  {PAYMENT_ICONS.map((p) => (
                    <img
                      key={p.alt}
                      src={p.src}
                      alt={p.alt}
                      className="h-7 w-auto object-contain"
                    />
                  ))}
                </Box>

                <Box className="mt-5 space-y-1 font-sans text-[12px] font-light leading-5 text-[#8a8a8a]">
                  <p>*Once your order has been placed no subsequent changes can be made in it.</p>
                  <p>*Shipping cost may vary depending on the delivery destination.</p>
                  <p>*Please check the final amount on the order summary page before completing the payment.</p>
                  <p>*An item's price may vary according to the size selected.</p>
                </Box>

                <Box className="mt-3 flex flex-wrap gap-5 font-sans text-[12px] font-normal tracking-wide text-[#BB3D1F]">
                  <button className="border-0 bg-transparent p-0 hover:underline" onClick={() => navigate("/shipping-policy")}>SHIPPING POLICY</button>
                  <button className="border-0 bg-transparent p-0 hover:underline" onClick={() => navigate("/terms-condition")}>HELP</button>
                  <button className="border-0 bg-transparent p-0 hover:underline" onClick={() => navigate("/contactUs")}>CONTACT US</button>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: { xs: 22, md: 24 },
                    fontWeight: 500,
                    color: "#222",
                    mb: 2,
                  }}
                >
                  Cart Summary
                </Typography>
                <Box className={`${summaryCard} p-5`}>
                  <Box className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={summaryLabel}>Cart Total</span>
                      <span className={summaryValue}>{convert(cart?.cart?.totalPrice) + " " + selectedCurrency}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#2f9e44]">
                      <span className="font-sans text-[14px] font-light">Total Discount</span>
                      <span className="font-sans text-[14px]">{"-" + convert(cart?.cart?.totalDiscount || 0) + " " + selectedCurrency}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#2f9e44]">
                      <span className="font-sans text-[14px] font-light">Shipping</span>
                      <span className="font-sans text-[14px]">Free</span>
                    </div>
                    {cart?.cart?.customizationCharge > 0 && (
                      <div className="flex items-center justify-between text-[#BB3D1F]">
                        <span className="font-sans text-[14px] font-light">Customization Charge</span>
                        <span className="font-sans text-[14px]">{"+" + convert(cart?.cart?.customizationCharge || 0) + " " + selectedCurrency}</span>
                      </div>
                    )}
                  </Box>
                </Box>

                <Box className={`${summaryCard} mt-4 p-5`}>
                  <Box className="flex items-center justify-between">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGiftItem}
                          onChange={(e) => setIsGiftItem(e.target.checked)}
                          size="small"
                          sx={{
                            color: "#cfcfcf",
                            "&.Mui-checked": { color: "#BB3D1F" },
                          }}
                        />
                      }
                      label={<span className="font-sans text-[13px] font-light text-[#333]">This is a gift item</span>}
                      className="m-0"
                    />
                    <Button
                      variant="text"
                      className="!text-[#BB3D1F] !text-[12px] !font-normal !normal-case hover:!bg-transparent"
                    >
                      (Know More)
                    </Button>
                  </Box>
                </Box>

                <Box className={`${summaryCard} mt-4 p-5`}>
                  <Typography
                    sx={{
                      fontFamily: "'Be Vietnam Pro', sans-serif",
                      fontSize: 13,
                      fontWeight: 400,
                      color: "#333",
                      mb: 1.5,
                      letterSpacing: "0.04em",
                    }}
                  >
                    COUPON CODE
                  </Typography>
                  <Box className="flex overflow-hidden rounded-lg border border-[#ececec]">
                    <TextField
                      placeholder="Enter Coupon Code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      size="small"
                      className="flex-1"
                      disabled={isCouponApply}
                      inputProps={{ maxLength: 16 }}
                      sx={{
                        "& fieldset": { border: "0 !important" },
                        "& input": {
                          fontFamily: "'Be Vietnam Pro', sans-serif",
                          fontSize: 13,
                        },
                      }}
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={isCouponApply}
                      className="!bg-[#333] hover:!bg-black !text-white !px-5 !rounded-none !text-[12px] !tracking-wide"
                      variant="contained"
                      size="medium"
                    >
                      APPLY
                    </Button>
                  </Box>
                </Box>

                <Box className={`${summaryCard} mt-4 p-5`}>
                  <Box className="mb-5 flex flex-wrap items-end justify-between gap-2">
                    <Typography
                      sx={{
                        fontFamily: "'Be Vietnam Pro', sans-serif",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#222",
                      }}
                    >
                      Total Payable
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Be Vietnam Pro', sans-serif",
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#222",
                      }}
                    >
                      {convert(cart?.cart?.cartTotalPrice)} {selectedCurrency}
                    </Typography>
                  </Box>

                  <Box className="space-y-3">
                    <Button
                      onClick={handleCheckout}
                      fullWidth
                      variant="contained"
                      className="!bg-[#BB3D1F] hover:!bg-[#9e3319] !text-white !py-3 !text-[13px] !font-semibold !normal-case !tracking-wide !rounded-lg !shadow-none"
                      startIcon={<CreditCardIcon />}
                      disabled={isLoading}
                    >
                      {isLoading ? "REDIRECTING..." : "PROCEED TO CHECKOUT"}
                    </Button>

                    <Button
                      onClick={() => navigate(-1)}
                      fullWidth
                      variant="outlined"
                      className="!py-3 !text-[13px] !font-normal !bg-white !border-[#ececec] !text-[#333] hover:!bg-[#faf7f4] !normal-case !tracking-wide !rounded-lg"
                      startIcon={<ShoppingCartIcon />}
                    >
                      CONTINUE SHOPPING
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <EmptyState
            imageSrc="https://cdn-icons-png.flaticon.com/512/2762/2762885.png"
            imageAlt="No Cart Product Available"
            title="Your Cart Is Empty!"
            subtitle="Add product to cart Then Proceed."
            buttonText="Start Shopping"
            buttonHref="/"
          />
        ))}

      {/* recent review products  */}
      <section className="py-4 mt-4 px-1">
        <RecentReviewProductsCarousel products={products.products?.response} />
      </section>
      {/* Trending Now Section */}
      <section className="py-4 mt-2 px-1">
        <HomeSectionGrid
          title="Trending Now"
          viewAllLink="/trending"
          products={products.products?.response}
        />

      </section>
    </Box>
  );
}

